/**
 * طبقة الاتصال بالسيستم.
 *
 * القرارات المنفَّذة هنا (docs/08-قرارات-فك-الموانع.md §5):
 *   • الجلسة كوكي HttpOnly ⇒ `credentials: "include"` على كل طلب،
 *     ومفيش أي توكن بيتخزّن في localStorage.
 *   • توكن CSRF إجباري في هيدر `X-CSRF-Token` على **كل كتابة**.
 *   • 401 ⇒ محاولة تحديث واحدة، وبعدين توجيه للدخول.
 *   • 403 ⇒ «مالكش صلاحية على ده».
 *
 * ⚠️ قاعدة المشروع: **ممنوع الفشل الصامت.** أي طلب بيفشل بيتسجّل بمستوى
 *    ERROR ومعاه المسار ورقم الطلب، والواجهة بتعرض السبب — مش بتبلع الخطأ.
 */
import { readCookie } from "@/lib/cookies";
import type { ApiErrorBody, Envelope } from "./types";

/**
 * جذر الـAPI. الواجهة والـAPI على **نفس الأصل** (nginx بيوصّل `/api/`
 * لجوّه شبكة كوليفاي) — عشان كده الكوكي HttpOnly بتشتغل ومفيش CORS أصلًا.
 * وحدة النشر بتحقن `VITE_API_BASE=/api` وقت البناء.
 */
const RAW_BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/+$/, "");
export const API_BASE = `${RAW_BASE}/v1`;

/** اسم كوكي الـCSRF المقروءة اللي بيبعتها الباك إند مع الجلسة */
const CSRF_COOKIE = "rtl_csrf";
const CSRF_HEADER = "X-CSRF-Token";

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * كتابات بتحصل **قبل** ما تبقى فيه جلسة — طبيعي إنها من غير توكن حماية.
 * ⚠️ مهم: من غير الاستثناء ده هنطلّع ERROR في كل محاولة دخول، والفريق
 *    بيتعوّد يتجاهل اللوج — وده بالظبط اللي بيخلّي الفشل الحقيقي يعدّي صامت.
 */
const PRE_SESSION_WRITES = [
  "/auth/login",
  "/auth/refresh",
  "/auth/password/forgot",
  "/auth/password/reset",
  // ⚠️ التسجيل الذاتي وتأكيد البريد بيحصلوا **قبل** أي جلسة، فمفيش توكن حماية.
  //    من غيرهم هنا كل محاولة تسجيل بتطلّع ERROR كاذب في اللوج — والفريق
  //    بيتعوّد يتجاهل السجل، وده اللي بيخلّي العطل الحقيقي يعدّي صامت.
  "/auth/register",
  "/auth/verify",
];

/** توكن CSRF محفوظ في الذاكرة — احتياطي لو الكوكي مش مقروءة من الجافاسكربت */
let csrfMemory: string | null = null;

export function rememberCsrf(token: string | null | undefined): void {
  if (token) csrfMemory = token;
}

export function currentCsrf(): string | null {
  return readCookie(CSRF_COOKIE) ?? csrfMemory;
}

export function forgetSession(): void {
  csrfMemory = null;
}

/** الخطأ الموحّد — `messageAr` هو النص المعروض للمستخدم */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly messageAr: string;
  readonly field: string | null;
  readonly details: Record<string, unknown> | null;
  readonly requestId: string | null;

  constructor(status: number, body: Partial<ApiErrorBody>, requestId: string | null) {
    const messageAr = body.message_ar?.trim() || fallbackMessageAr(status);
    super(`${body.code ?? "HTTP_" + status}: ${messageAr}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? `HTTP_${status}`;
    this.messageAr = messageAr;
    this.field = body.field ?? null;
    this.details = body.details ?? null;
    this.requestId = body.request_id ?? requestId;
  }
}

/** انقطاع الشبكة — حالة شاشة مستقلة («مفيش اتصال») */
export class OfflineError extends Error {
  readonly messageAr = "مفيش اتصال بالسيستم دلوقتي";
  constructor(cause?: unknown) {
    super("network unreachable");
    this.name = "OfflineError";
    if (cause) this.cause = cause;
  }
}

/**
 * نصوص احتياطية — **بتتستخدم بس لما السيرفر ما يبعتش `message_ar`**.
 * القاعدة: الواجهة ما بتخترعش رسالة لو السيرفر بعت واحدة.
 */
function fallbackMessageAr(status: number): string {
  switch (status) {
    case 400:
      return "فيه بيانات ناقصة أو غلط في الطلب";
    case 401:
      return "الجلسة انتهت — لازم تسجّل دخول تاني";
    case 403:
      return "مالكش صلاحية على ده";
    case 404:
      return "مالقيناش اللي بتدوّر عليه";
    case 409:
      return "حد تاني عدّل نفس السجل — افتحه تاني وحاول";
    case 423:
      return "الحساب اتقفل مؤقتًا — التواصل مع إدارة الأكاديمية";
    case 429:
      return "محاولات كتير في وقت قصير — استنى شوية";
    default:
      return status >= 500
        ? "مقدرناش نجيب البيانات"
        : "حصلت مشكلة مش متوقعة";
  }
}

/** بيتبعت لما الجلسة تخلص — SessionProvider بيسمعه ويوجّه للدخول */
export const UNAUTHORIZED_EVENT = "rtl:unauthorized";

function announceUnauthorized(): void {
  window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
}

export type RequestOptions = {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** القفل التفاؤلي — `version` بتاعة المستند */
  ifMatch?: number | string;
  /** إجباري على بعض المسارات (claim · ledger · commit · leads) */
  idempotencyKey?: string;
  headers?: Record<string, string>;
  /** داخلي: منع لفّة تحديث لا نهائية */
  _retried?: boolean;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = path.startsWith(`${RAW_BASE}/`) ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

/**
 * نتيجة محاولة تجديد الجلسة.
 * ⚠️ التفرقة دي هي بيت القصيد: **مفيش اتصال ≠ الجلسة خلصت**. لو رجّعنا
 *    «فشل» واحد للحالتين، الموظف بيتطرد لشاشة الدخول لمجرد إن السيستم
 *    غمز لحظة — وشاشة الدخول نفسها هتفشل لأن السيستم أصلًا واقع.
 */
type RefreshOutcome = "renewed" | "expired" | "unreachable";

/** تحديث الجلسة — طلب واحد بس حتى لو 10 طلبات وقعوا مع بعض */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

async function refreshSession(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = (async (): Promise<RefreshOutcome> => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: csrfHeaders(),
        });
        if (res.status >= 500) {
          // عطل في السيستم — ما ينفعش يتحسب «جلسة انتهت»
          console.error(`[api] تجديد الجلسة فشل (${res.status} POST ${API_BASE}/auth/refresh)`);
          return "unreachable";
        }
        if (!res.ok) return "expired";
        const body = (await res.json().catch(() => null)) as
          | { data?: { csrf_token?: string } }
          | null;
        rememberCsrf(body?.data?.csrf_token);
        return "renewed";
      } catch (err) {
        // ⛔ ممنوع الفشل الصامت — ده كان `catch { return false }`
        console.error(`[api] الشبكة فشلت وإحنا بنجدّد الجلسة (POST ${API_BASE}/auth/refresh)`, err);
        return "unreachable";
      } finally {
        // بيتصفّر بعد الـtick عشان كل المنتظرين ياخدوا نفس النتيجة
        setTimeout(() => (refreshInFlight = null), 0);
      }
    })();
  }
  return refreshInFlight;
}

function csrfHeaders(): Record<string, string> {
  const token = currentCsrf();
  return token ? { [CSRF_HEADER]: token } : {};
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<Envelope<T>> {
  const method = (opts.method ?? "GET").toUpperCase();
  const isWrite = WRITE_METHODS.has(method);
  const url = buildUrl(path, opts.query);

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  // ⚠️ **`FormData` بيعدّي زي ما هو**: المتصفح بيحط `Content-Type` بنفسه
  //    ومعاه الحدّ الفاصل (boundary). لو حطّيناه إحنا، السيرفر ما يعرفش
  //    يفصل الأجزاء والرفع بيترفض برسالة مضلّلة عن «حقل ناقص».
  const isForm = typeof FormData !== "undefined" && opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) {
    headers["Content-Type"] = "application/json; charset=utf-8";
  }
  if (opts.ifMatch !== undefined) headers["If-Match"] = `"${opts.ifMatch}"`;
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  if (isWrite) {
    const token = currentCsrf();
    if (token) {
      headers[CSRF_HEADER] = token;
    } else if (!PRE_SESSION_WRITES.some((p) => path.startsWith(p))) {
      // ⚠️ ممنوع الفشل الصامت: من غير التوكن ده الكتابة هتترفض من السيرفر.
      //    بنصرخ في اللوج، وبنكمّل عشان رسالة السيرفر توصل للمستخدم بدل صمت.
      console.error(
        `[api] كتابة بلا توكن حماية (${method} ${url}) — كوكي "${CSRF_COOKIE}" مش موجودة أو مش مقروءة.`,
      );
    }
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "include", // ⚠️ الجلسة كوكي HttpOnly — بلا ده مفيش مصادقة
      headers,
      body:
        opts.body === undefined
          ? undefined
          : isForm
            ? (opts.body as FormData)
            : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    console.error(`[api] الشبكة فشلت (${method} ${url})`, err);
    throw new OfflineError(err);
  }

  const requestId = res.headers.get("X-Request-Id");

  if (res.status === 204) return { data: undefined as T };

  const raw = await res.text();
  let parsed: unknown = null;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      // رد مش JSON — غالبًا صفحة خطأ من البروكسي. ده فشل حقيقي مش حالة عادية.
      console.error(`[api] رد مش JSON (${res.status} ${method} ${url})`, raw.slice(0, 300));
      throw new ApiError(res.status || 500, {}, requestId);
    }
  }

  if (res.ok) {
    const env = (parsed ?? { data: null }) as Envelope<T>;
    rememberCsrf((env.data as { csrf_token?: string } | null)?.csrf_token);
    return env;
  }

  // 🔴 السيرفر بيرد بشكلين: `{error: {...}}` (مسارات المحرّك) و`{code, message_ar, details}`
  //    مسطّح (التحقق من الحقول `main.py::validation_error` و`HTTPException`).
  //    الواجهة كانت بتقرا الأول بس، فأي رفض من التاني بيظهر للموظفة
  //    «حصلت مشكلة مش متوقعة» وسببه الحقيقي ضايع.
  const errObj = parsed as { error?: ApiErrorBody; code?: string; message_ar?: string } | null;
  const errBody = (errObj?.error ??
    (typeof errObj?.message_ar === "string" ? errObj : {})) as Partial<ApiErrorBody>;

  // 401 — محاولة تحديث واحدة، وبعدين توجيه للدخول.
  // ⚠️ الدخول والتحديث نفسهم مستثنيين وإلا بندخل في لفّة.
  const isAuthEdge = path.startsWith("/auth/login") || path.startsWith("/auth/refresh");
  if (res.status === 401 && !opts._retried && !isAuthEdge) {
    // مفيش أي أثر لجلسة على الجهاز ⇒ ده زائر مش جلسة انتهت.
    // ⚠️ من غير الفرق ده بنضرب /auth/refresh بلا داعي وبنطلّع ERROR على
    //    حالة طبيعية تمامًا — والفريق بيتعوّد يتجاهل اللوج.
    if (!currentCsrf()) {
      console.info(`[api] مفيش جلسة على الجهاز ده (${method} ${url}) — شاشة الدخول`);
      forgetSession();
      announceUnauthorized();
      throw new ApiError(401, errBody, requestId);
    }
    const outcome = await refreshSession();
    if (outcome === "renewed") return request<T>(path, { ...opts, _retried: true });
    if (outcome === "unreachable") {
      // السيستم واقع أو النت قاطع — الموظف بيفضل مكانه ويعيد المحاولة،
      // وما بيتطردش لشاشة الدخول.
      throw new OfflineError();
    }
    console.error(`[api] الجلسة انتهت (${method} ${url})`);
    forgetSession();
    announceUnauthorized();
    throw new ApiError(401, errBody, requestId);
  }
  if (res.status === 401 && !path.startsWith("/auth/login")) {
    // انتهاء جلسة على مسار الدخول/التحديث نفسه — نظّف وودّي على الدخول
    forgetSession();
    announceUnauthorized();
  }

  const error = new ApiError(res.status, errBody, requestId);
  console.error(
    `[api] ${res.status} ${error.code} (${method} ${url})` +
      (error.requestId ? ` request_id=${error.requestId}` : ""),
    error.details ?? "",
  );
  throw error;
}

export const api = {
  get: <T>(path: string, opts: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...opts, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...opts, method: "PUT", body }),
};

/** النص المعروض لأي خطأ — من السيرفر لو بعت، وإلا نص احتياطي */
export function errorMessageAr(err: unknown): string {
  if (err instanceof ApiError) return err.messageAr;
  if (err instanceof OfflineError) return err.messageAr;
  return "حصلت مشكلة مش متوقعة";
}
